import SEOHead from "@/components/SEOHead";
import { Overline } from "@/components/Section";
import { Link } from "react-router-dom";

const SECTIONS = [
  { id: "agreement",       label: "Agreement to Terms" },
  { id: "about",           label: "About Hylas Companion" },
  { id: "website-use",     label: "Website Use" },
  { id: "application",     label: "Application Process" },
  { id: "representation",  label: "Representation Agreement" },
  { id: "agency-auth",     label: "Agency Representation & Multi-Carrier Authorization" },
  { id: "no-guarantee",    label: "No Employment Guarantee" },
  { id: "accuracy",        label: "Accuracy of Information" },
  { id: "ip",              label: "Intellectual Property" },
  { id: "liability",       label: "Limitation of Liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "governing-law",   label: "Governing Law" },
  { id: "changes",         label: "Changes to Terms" },
  { id: "contact",         label: "Contact" },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 mb-12 pb-12 border-b border-border last:border-0 last:mb-0 last:pb-0">
      <h2 className="font-head text-xl md:text-2xl font-bold uppercase tracking-[0.12em] text-[#0A0A0A] mb-4">{title}</h2>
      <div className="space-y-4 text-foreground/75 text-base leading-relaxed">{children}</div>
    </section>
  );
}

export default function TermsOfService() {
  return (
    <>
      <SEOHead
        title="Terms of Service | Hylas Companion"
        description="Terms of Service for Hylas Companion — Andrej Sasaroga DBA Hylas Companion. Covers website use, driver application process, representation agreement, and limitation of liability."
        path="/terms"
      />

      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-40 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Overline light>Legal</Overline>
          <h1 className="font-head text-5xl sm:text-6xl font-bold uppercase tracking-[0.12em] text-white mt-5 leading-none">
            Terms of<br /><span className="text-primary">Service</span>
          </h1>
          <p className="mt-6 text-white/50 text-sm">
            Effective Date: February 20, 2026 &nbsp;·&nbsp; Last Updated: October 30, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-16 xl:gap-24">

          {/* Sticky TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <p className="font-head text-[10px] uppercase tracking-[0.35em] text-primary mb-4">Contents</p>
              <nav className="space-y-2">
                {SECTIONS.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block text-sm text-foreground/55 hover:text-primary transition-colors duration-200 leading-snug"
                  >
                    {s.label}
                  </a>
                ))}
              </nav>
              <div className="mt-10 pt-8 border-t border-border">
                <p className="text-xs text-foreground/40 leading-relaxed">
                  Questions? <a href="mailto:contact@hylascompanion.com" className="text-primary hover:underline">contact@hylascompanion.com</a>
                </p>
              </div>
            </div>
          </aside>

          {/* Body */}
          <article className="min-w-0">

            <Section id="agreement" title="Agreement to Terms">
              <p>
                By accessing or using the websites located at <strong>hylascompanion.us and hylascompanion.com</strong> (collectively, the "Site") and any services offered through the Site, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Site.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you and <strong>Andrej Sasaroga, doing business as Hylas Companion</strong> ("Company," "we," "us," or "our"). Your continued use of the Site constitutes acceptance of any updates to these Terms.
              </p>
            </Section>

            <Section id="about" title="About Hylas Companion">
              <p>
                Hylas Companion is a recruitment and placement company operated by <strong>Andrej Sasaroga DBA Hylas Companion</strong>. We act as an intermediary between commercial truck drivers holding valid CDL-A (or other applicable) licenses and motor carriers seeking qualified drivers. We run the Hylas Market — a platform where partner motor carriers compete for qualified drivers. We do not own or operate trucks, and we do not directly employ drivers as a motor carrier.
              </p>
              <p>
                Our business address and contact information are available on our <Link to="/contact" className="text-primary hover:underline">Contact page</Link>.
              </p>
            </Section>

            <Section id="website-use" title="Website Use">
              <p>
                The Site and its content are provided for informational and recruitment purposes only. You agree to use the Site only for lawful purposes and in a manner consistent with these Terms. You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Use the Site in any way that violates applicable federal, state, local, or international laws or regulations.</li>
                <li>Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Site.</li>
                <li>Use automated tools, scripts, bots, or scrapers to access, copy, or harvest content from the Site without prior written consent.</li>
                <li>Introduce malware, viruses, or other harmful code to the Site or its underlying systems.</li>
                <li>Impersonate any person or entity, or falsely state or misrepresent your affiliation with any person or entity.</li>
                <li>Post or transmit unsolicited commercial communications (spam).</li>
              </ul>
              <p>
                We reserve the right to terminate or restrict your access to the Site at our sole discretion, without notice, for conduct that we determine violates these Terms or is otherwise harmful to other users, us, or third parties.
              </p>
            </Section>

            <Section id="application" title="Driver Application Process">
              <p>
                By submitting an application through the Site, you represent and warrant that:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>All information provided in your application is true, complete, and accurate to the best of your knowledge.</li>
                <li>You hold a valid commercial driver's license (CDL-A or other applicable class) in the state in which you are licensed, or you are otherwise eligible for a position you have applied for.</li>
                <li>You authorize Hylas Companion to obtain and review your motor vehicle record (MVR), Pre-Employment Screening Program (PSP) report, driving history, employment verifications, and any other background information relevant to your application.</li>
                <li>You understand that submission of an application does not guarantee placement, employment, or any contractual relationship with a carrier.</li>
              </ul>
              <p>
                You acknowledge that Hylas Companion may share your application data with one or more motor carriers and third-party screening platforms, including but not limited to <strong>Tenstreet</strong> and <strong>IntelliApp</strong>, for the purpose of evaluating your application. Please review our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> for full details on how your data is handled.
              </p>
              <p>
                By submitting an application, you also agree to be contacted by Hylas Companion and its recruiters by phone call, text message, and email as described in the <Link to="/privacy#calls-texts" className="text-primary hover:underline">Calls &amp; Text Messages</Link> section of our Privacy Policy.
              </p>
              <p>
                Submitting false, misleading, or incomplete information in your application may result in immediate disqualification, termination of any placement, and may expose you to legal liability.
              </p>
            </Section>

            <Section id="representation" title="Representation Agreement">
              <p>
                By submitting an application through Hylas Companion, you grant Hylas Companion a non-exclusive right to represent you and submit your driver profile to motor carriers on your behalf during the active representation period.
              </p>
              <p>
                <strong>Application Sharing Authorization:</strong> When you sign and submit an Application Sharing Authorization through our application form, you agree not to submit your driver profile, application materials, or resume to the same carriers to which Hylas Companion has introduced you, through any other recruiting agency or intermediary, for a period of <strong>45 days</strong> from the date of first introduction to a partner carrier, and for <strong>12 months</strong> from the date of hire if placed by Hylas Companion at a carrier. This exclusivity applies only to the specific carriers to which Hylas Companion has made an introduction and does not restrict your right to apply to other carriers independently.
              </p>
              <p>
                <strong>No Agency Fees:</strong> Drivers are never charged a fee for representation, application processing, or placement services. Hylas Companion is compensated solely by the carriers with which it places drivers.
              </p>
              <p>
                <strong>Right to Withdraw:</strong> You may withdraw your application or end your representation by Hylas Companion at any time by written notice to <a href="mailto:contact@hylascompanion.com" className="text-primary hover:underline">contact@hylascompanion.com</a>. Withdrawal does not retroactively affect any Application Sharing Authorization already signed and in effect.
              </p>
              <p>
                <strong>No Agency Relationship:</strong> This representation arrangement does not create an employer-employee, partnership, joint venture, or any other employment-type relationship between you and Hylas Companion. Hylas Companion acts solely as a recruitment intermediary.
              </p>
            </Section>

            <Section id="reassignment" title="Reassignment & Placement Rights">
              <p>
                <strong>Right of Reassignment and Placement Flexibility:</strong> By signing this Agreement, the Driver acknowledges and agrees that Hylas Companion reserves the sole and exclusive right, at its discretion, to reassign, transfer, or transition the Driver to alternative carriers, equipment opportunities, or partner fleet deals during a period of twelve (12) months from the date of execution. Such determination may be made by Hylas Companion if it deems the reassignment to be in the mutual best interest of both Hylas Companion and the Driver. The Driver agrees to reasonably cooperate with Hylas Companion in completing any necessary onboarding steps or documentation required for such reassignment.
              </p>
            </Section>

            <Section id="agency-auth" title="Agency Representation & Multi-Carrier Authorization">
              <p>
                By signing the <strong>Multi-Carrier Application Authorization</strong> within the Hylas Companion application form, drivers expressly authorize Hylas Companion to act as their <strong>Limited Attorney-in-Fact</strong> for the purpose of submitting their Driver Qualification Packet to any contracted partner motor carrier as a complete, legally binding substitute for a carrier-specific employment application.
              </p>
              <p>
                This authorization operates as follows:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong>FMCSA Compliance:</strong> The Hylas Companion Driver Qualification Packet is compiled to satisfy the mandatory data elements of 49 CFR §391.21, the federal standard for pre-employment driver applications. A receiving motor carrier may place this packet directly into their Driver Qualification (DQ) file to satisfy regulatory requirements without requiring the driver to fill out a separate carrier application (e.g., IntelliApp, Tenstreet, or paper forms).</li>
                <li><strong>Consent Mirroring:</strong> The driver's executed FCRA disclosure, Clearinghouse consent, Limited Power of Attorney, and E-SIGN authorization — as signed within the Hylas Companion application — are legally mirrored to all contracted partner carriers. The driver's signature has the same force and effect as if the driver had personally executed the carrier's own form.</li>
                <li><strong>Scope:</strong> This authorization covers submission to motor carriers with whom Hylas Companion has an active recruiting or placement agreement. It does not authorize Hylas Companion to submit driver data to carriers outside this contracted network without the driver's separate written consent.</li>
                <li><strong>Duration:</strong> The Multi-Carrier Authorization is valid for one (1) year from the date the application was executed. Drivers may revoke this authorization at any time in writing to <a href="mailto:contact@hylascompanion.com" className="text-primary hover:underline">contact@hylascompanion.com</a>.</li>
                <li><strong>10-Year DOT Compliance:</strong> The initial Hylas Companion application collects three (3) years of commercial employment history for speed of application. Under 49 CFR §391.21, a carrier's DQ file must contain ten (10) years of history before a driver is dispatched. When a carrier requires the full 10-year history, Hylas Companion will request a supplemental 7-year addendum from the driver via the Driver Hub. This two-phase process keeps initial application conversion high while maintaining full DOT compliance before dispatch.</li>
              </ul>
              <p>
                Drivers who do not wish to grant this Multi-Carrier Authorization must notify Hylas Companion in writing before their application is submitted to any partner carrier. Opting out of this authorization will not affect the driver's representation by Hylas Companion, but will require the driver to complete a carrier-specific application for each individual carrier placement.
              </p>
            </Section>

            <Section id="no-guarantee" title="No Employment Guarantee">
              <p>
                Hylas Companion makes no guarantee, representation, or warranty that submitting an application or entering into a representation arrangement will result in employment, a lease arrangement, or any contractual relationship with any motor carrier. Driver placement is subject to the hiring criteria, availability, and sole discretion of the motor carriers to which applications are submitted.
              </p>
              <p>
                Pay rates, mileage rates, bonuses, and compensation structures described on the Site are provided for informational purposes and represent ranges currently available in the market. Actual compensation will depend on the specific terms agreed upon between you and the motor carrier.
              </p>
            </Section>

            <Section id="accuracy" title="Accuracy of Information">
              <p>
                We make reasonable efforts to ensure that information on the Site regarding pay packages, available positions, operating areas, and equipment is current and accurate. However, trucking industry conditions change frequently. All rates, positions, and program details are subject to change without notice. You are encouraged to confirm current details directly with Hylas Companion before making any career or financial decisions.
              </p>
              <p>
                The interactive tools on this Site (including the Gross Pay Calculator and Settlement Calculator) are provided as educational estimates only. They are not financial advice and do not constitute a guarantee of earnings.
              </p>
            </Section>

            <Section id="ip" title="Intellectual Property">
              <p>
                All content on this Site — including text, graphics, logos, images, design elements, page layouts, and software — is the property of Andrej Sasaroga DBA Hylas Companion and is protected by applicable copyright, trademark, and intellectual property laws. See our <Link to="/copyright" className="text-primary hover:underline">Copyright Policy</Link> for full details.
              </p>
              <p>
                Nothing on this Site grants you any license or right to use any trademark, logo, or branded content without prior written permission.
              </p>
            </Section>

            <Section id="liability" title="Limitation of Liability">
              <p>
                TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, ANDREJ SASAROGA DBA HYLAS COMPANION, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SITE OR SERVICES, INCLUDING BUT NOT LIMITED TO LOSS OF REVENUE, LOSS OF EARNINGS, LOSS OF DATA, OR LOSS OF BUSINESS OPPORTUNITIES.
              </p>
              <p>
                IN NO EVENT SHALL OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR YOUR USE OF THE SITE EXCEED ONE HUNDRED DOLLARS ($100.00 USD).
              </p>
              <p>
                SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN WARRANTIES OR DAMAGES, SO THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU IN THEIR ENTIRETY.
              </p>
              <p>
                THE SITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>
            </Section>

            <Section id="indemnification" title="Indemnification">
              <p>
                You agree to indemnify, defend, and hold harmless Andrej Sasaroga DBA Hylas Companion and its officers, directors, agents, and employees from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Site; (b) your violation of these Terms; (c) any false, misleading, or inaccurate information you submit; or (d) your violation of any third-party rights.
              </p>
            </Section>

            <Section id="governing-law" title="Governing Law & Dispute Resolution">
              <p>
                These Terms are governed by and construed in accordance with the laws of the <strong>State of Illinois</strong>, without regard to its conflict of law provisions.
              </p>
              <p>
                Any dispute arising under these Terms shall first be addressed through good-faith negotiation. If unresolved, disputes shall be submitted to binding arbitration in <strong>Cook County, Illinois</strong>, under the commercial arbitration rules of the American Arbitration Association. Class action proceedings are waived to the fullest extent permitted by law.
              </p>
              <p>
                You agree that any claims must be brought within one (1) year of the date the claim arose, or such claim shall be permanently barred.
              </p>
            </Section>

            <Section id="changes" title="Changes to These Terms">
              <p>
                We reserve the right to update or modify these Terms at any time. Changes will be posted on this page with an updated "Last Updated" date. Your continued use of the Site following any changes constitutes your acceptance of the revised Terms.
              </p>
              <p>
                We encourage you to review these Terms periodically. Material changes that affect your rights may be communicated via a notice on the Site's homepage.
              </p>
            </Section>

            <Section id="contact" title="Contact">
              <p>
                If you have questions about these Terms, please contact us:
              </p>
              <div className="bg-[#0A0A0A]/[0.04] border border-border p-6 mt-2">
                <p className="font-head text-xs uppercase tracking-[0.25em] text-primary mb-3">Hylas Companion</p>
                <p className="text-sm">Andrej Sasaroga DBA Hylas Companion</p>
                <p className="text-sm mt-1">
                  Email: <a href="mailto:contact@hylascompanion.com" className="text-primary hover:underline">contact@hylascompanion.com</a>
                </p>
                <p className="text-sm mt-1">
                  Phone: <a href="tel:7083959565" className="text-primary hover:underline">(708) 395-9565</a>
                </p>
              </div>
            </Section>

          </article>
        </div>
      </div>
    </>
  );
}
